import { Switch, Route, useParams, useLocation, Redirect, Link } from "wouter";
import { BrandUrlRouting } from "@/lib/brand-url-routing";
import { SmbAdminGuard } from "@/components/smb-admin-guard";
export { SmbAdminGuard } from "@/components/smb-admin-guard";
import { AlertCircle, LoaderCircle, Lock } from "lucide-react";
import { apiRequest, queryClient } from "./lib/queryClient";
import { QueryClientProvider, useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/app-sidebar";
import { useAuth } from "@/hooks/use-auth";
import NotFound from "@/pages/not-found";
import LandingPage from "@/pages/landing";
import HomeV2Page from "@/pages/home-v2";
import DashboardPage from "@/pages/dashboard";
import BrandsPage from "@/pages/brands";
import BrandDetailPage from "@/pages/brand-detail";
import CustomerEvidencePage from "@/pages/customer-evidence";
import PersonasPage from "@/pages/personas";
import PainPillarsPage from "@/pages/pain-pillars";
import PersonaDetailPage from "@/pages/persona-detail";
import ProductsPage from "@/pages/products";
import ProductDetailPage from "@/pages/product-detail";
import AdUnitsPage from "@/pages/ad-units";
import AdsExpressPage from "@/pages/ads-express";
import AdsExpressKcPage from "@/pages/ads-express-kc";
import LandingPagesExpressPage from "@/pages/landing-pages-express";
import EmailExpressPage from "@/pages/email-express";
import EmailFlowPage from "@/pages/email-flow";
import EmailEditorPage from "@/pages/email-editor";
// Email Express 2.0 (Task #2701) — fully additive, separate from V1 above.
import EmailExpress2CreatePage from "@/pages/email-express-2-create";
import EmailExpress2EditorPage from "@/pages/email-express-2-editor";
import LpExpressPreviewPage from "@/pages/lp-express-preview";
import AdLibraryPage from "@/pages/ad-library";
import AdAnimatorPage from "@/pages/ad-animator";
import UgcTemplatesPage from "@/pages/ugc-templates";
import AdUnitDetailPage from "@/pages/ad-unit-detail";
import OffersPage from "@/pages/offers";
import OfferCreatePage from "@/pages/offer-create";
import OfferDetailPage from "@/pages/offer-detail";
import SalesDiscountsPage from "@/pages/sales-discounts";
import SalesDiscountCreatePage from "@/pages/sales-discount-create";
import SalesDiscountDetailPage from "@/pages/sales-discount-detail";
import StrategyMatrixPage from "@/pages/strategy-matrix";
import CreativeLabPage from "@/pages/creative-lab";
import PerformancePage from "@/pages/performance";
import LearningsPage from "@/pages/learnings";
import NarrativesPage from "@/pages/narratives";
import NarrativeGenerationDetailPage from "@/pages/narrative-generation-detail";
import NarrativeAngleDetailPage from "@/pages/narrative-angle-detail";
import CampaignsPage from "@/pages/campaigns";
import CampaignDetailPage from "@/pages/campaign-detail";
import AssetsPage from "@/pages/assets";
import PersonaCreatePage from "@/pages/persona-create";
import ProductCreatePage from "@/pages/product-create";
import IdeatorListPage from "@/pages/ideator-list";
import IdeatorCreatePage from "@/pages/ideator-create";
import IdeatorDetailPage from "@/pages/ideator-detail";
import CampaignWizardPage from "@/pages/campaign-wizard";
import BrandWizardPage from "@/pages/brand-wizard";
import AdminPage from "@/pages/admin";
import AdminCampaignAlertsPage from "@/pages/admin-campaign-alerts";
import BrandsDashboardPage from "@/pages/brands-dashboard";
import AdminLeadsPage from "@/pages/admin-leads";
import AdminBetaFeaturesPage from "@/pages/admin-beta-features";
import AdminShopifyImageRepairPage from "@/pages/admin-shopify-image-repair";
import AdminTemplateStudioTypesPage from "@/pages/admin-template-studio-types";
import AdminSmbPage from "@/pages/admin-smb";
import AdminTextAgentPage from "@/pages/admin-text-agent";
import MessagingResultPage from "@/pages/messaging-result";
import BoardReferenceLibraryPage from "@/pages/template-studio-library";
import ConceptBoardsPage from "@/pages/concept-boards";
import InternalOnboardingPage from "@/pages/internal-onboarding";
import InternalMcpConsolePage from "@/pages/internal-mcp-console";
import CreativeTinderPage from "@/pages/creative-tinder";
import CreativeTinderSettingsPage from "@/pages/creative-tinder-settings";
import ProfilePage from "@/pages/profile";
import SecurityPage from "@/pages/security";
import UsageLogsPage from "@/pages/usage-logs";
import BrandSpyPage from "@/pages/brand-spy";
import UgcCampaignsPage from "@/pages/ugc-campaigns";
import PixarCampaignsPage from "@/pages/pixar-campaigns";
import PixarCampaignDetailPage from "@/pages/pixar-campaign-detail";
import PixarCampaignCreatePage from "@/pages/pixar-campaign-create";

import SimpsonsCampaignsPage from "@/pages/simpsons-campaigns";
import SimpsonsCampaignDetailPage from "@/pages/simpsons-campaign-detail";
import SimpsonsCampaignCreatePage from "@/pages/simpsons-campaign-create";

import DayinlifeCampaignsPage from "@/pages/dayinlife-campaigns";
import DayinlifeCampaignDetailPage from "@/pages/dayinlife-campaign-detail";
import DayinlifeCampaignCreatePage from "@/pages/dayinlife-campaign-create";
import UgcCampaignDetailPage from "@/pages/ugc-campaign-detail";
import UgcCampaignCreatePage from "@/pages/ugc-campaign-create";
import ClaymationCampaignsPage from "@/pages/claymation-campaigns";
import ClaymationCampaignDetailPage from "@/pages/claymation-campaign-detail";
import ClaymationCampaignCreatePage from "@/pages/claymation-campaign-create";
import VideoExpressCampaignsPage from "@/pages/video-express-campaigns";
import UgcExpressPage from "@/pages/ugc-express";
import SlideshowExpressPage from "@/pages/slideshow-express";
import MemeGeneratorPage from "@/pages/meme-generator";
import VideoExpressCampaignEditPage from "@/pages/video-express-campaign-edit";
import LandingPagesPage from "@/pages/landing-pages";
import LandingPageDetailPage from "@/pages/landing-page-detail";
import LandingPageTemplatesPage from "@/pages/landing-page-templates";
import LandingPageTemplateDetailPage from "@/pages/landing-page-template-detail";
import InternalUpdatesPage from "@/pages/internal-updates";
import MetaAdsIntegrationPage from "@/pages/meta-ads-integration";
import AdsPerformanceBriefPage from "@/pages/ads-performance-brief";
import AdsPerformanceOverviewPage from "@/pages/ads-performance-overview";
import PersonaIntelligencePage from "@/pages/persona-intelligence";
import PersonaLabDashboardPage from "@/pages/persona-lab-dashboard";
import { lazy, Suspense } from "react";
import CreativePerformancePage from "@/pages/creative-performance";
import { CreativeStrategistGuard } from "@/components/creative-strategist-guard";
import { EmailExpress2Guard } from "@/components/email-express-2-guard";
import CreativeStrategistHomePage from "@/pages/creative-strategist";
import CreativeStrategistProjectsPage from "@/pages/creative-strategist-projects";
import CreativeStrategistProjectPage from "@/pages/creative-strategist-project";
import CreativeStrategistTemplatesPage from "@/pages/creative-strategist-templates";
import BrollExpressPage from "@/pages/broll-express";
import VideoReferenceAdsPage from "@/pages/video-reference-ads";
import VideoReferenceTemplateLibraryPage from "@/pages/video-reference-template-library";
import GrwmugcPbPage from "@/pages/grwmugc-pb";
import DeliverablesPage, { WeeklyDeliverablesGuard } from "@/pages/deliverables";
import DeliverableReviewPage from "@/pages/deliverable-review";
import CreativeIterationsPage from "@/pages/creative-iterations";
import GrowthOperationsPage from "@/pages/growth-operations";
import TemplateStudioPage, { TemplateStudioLibraryPage } from "@/pages/internal/template-studio";
const VentureDeckPage = lazy(() => import("@/pages/venture-deck"));
const SalesDeckPage = lazy(() => import("@/pages/sales-deck"));
const PublicLpExpressPage = lazy(() => import("@/pages/public-lp-express"));
const ContactPage = lazy(() => import("@/pages/contact"));
const ClientIntakePage = lazy(() => import("@/pages/client-intake"));
const loadAdLauncherPage = () => import("@/pages/ad-launcher");
// Keep in-app navigation lazy, but on a direct/refreshed launcher URL begin
// fetching the route chunk as soon as the app module executes instead of
// waiting for auth and brand guards to finish rendering.
const directAdLauncherPage = window.location.pathname.endsWith("/ad-launcher")
  ? loadAdLauncherPage()
  : null;
const AdLauncherPage = lazy(() => directAdLauncherPage ?? loadAdLauncherPage());
import type { Brand } from "@shared/schema";
import { getQueryFn } from "@/lib/queryClient";
import { Skeleton } from "@/components/ui/skeleton";
import { NotificationBell } from "@/components/notification-bell";
import { AIResearchTracker } from "@/hooks/use-ai-research-tracker";
import {
  parseVideoReferenceAvailability,
  videoReferenceAvailabilityGuardState,
} from "@/lib/video-reference-availability";
import { LP_EXPRESS_2_ROUTE } from "@/lib/lp-express-surface";
import { globalTemplateStudioPath, LP_TEMPLATE_STUDIO_PATH } from "@/lib/lp-template-studio-routing";
import { z } from "zod";

const CreativeEditorPage = lazy(() => import("@/pages/creative-editor"));

function BrandPageWrapper({ children }: { children: React.ReactNode }) {
  const { brandId } = useParams<{ brandId: string }>();
  const { data: brand } = useQuery<Brand>({
    queryKey: ["/api/brands", brandId],
    queryFn: getQueryFn({ on401: "returnNull" }),
    enabled: !!brandId,
  });

  return (
    <SidebarProvider style={{ "--sidebar-width": "16rem", "--sidebar-width-icon": "3rem" } as React.CSSProperties}>
      <div className="flex h-screen w-full">
        <AppSidebar brandId={brandId} brandName={brand?.name} />
        <div className="flex flex-col flex-1 min-w-0">
          <header className="flex items-center justify-between p-2 border-b shrink-0 h-12">
            <SidebarTrigger data-testid="button-sidebar-toggle" />
            <NotificationBell />
          </header>
          <main className="flex-1 min-h-0 overflow-y-auto">
            {children}
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}

// Admin-only routes (Creative Tinder, Landing Pages Express, UGC Video
// Campaigns, Pixar Video Campaigns). Direct URL hits redirect to the brand
// overview (or home) instead of 404, so links shared in error don't reveal
// the gated page.
export function AdminOnlyGuard({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();
  const { brandId } = useParams<{ brandId?: string }>();
  if (isLoading) return null;
  if (!user?.isMaster) {
    return <Redirect to={brandId ? `/brands/${brandId}` : "/"} />;
  }
  return <>{children}</>;
}

// Brand-scoped tools without a feature flag still fail closed on the client:
// the shared brand endpoint is authenticated and resource-gated by the server.
export function BrandAccessGuard({ children }: { children: React.ReactNode }) {
  const { user, isLoading: authLoading } = useAuth();
  const { brandId } = useParams<{ brandId?: string }>();
  const {
    data: brand,
    isLoading: brandLoading,
    isFetching,
    isFetchedAfterMount,
    isError,
  } = useQuery<Brand>({
    queryKey: ["/api/brands", brandId, "creative-iterations-access", user?.id],
    queryFn: async () => {
      const response = await fetch(`/api/brands/${brandId}`, { credentials: "include" });
      if (!response.ok) throw new Error("Not allowed");
      return response.json();
    },
    enabled: !!brandId && !!user && !authLoading,
    retry: false,
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: "always",
  });
  if (authLoading || (!!brandId && !!user && (brandLoading || isFetching || !isFetchedAfterMount))) return null;
  if (!user || !brandId || isError || !brand) {
    return <Redirect to={user && brandId ? `/brands/${brandId}` : "/"} />;
  }
  return <>{children}</>;
}

// Creative Performance Dashboard guard — allows any authenticated brand user
// when CP is enabled for the brand. Queries the server-gated status route
// (fail-closed: renders nothing while loading, redirects on 403/error).
// Masters pass through automatically via hasResourceAccess on the server.
export function CreativePerformanceGuard({ children }: { children: React.ReactNode }) {
  const { user, isLoading: authLoading } = useAuth();
  const { brandId } = useParams<{ brandId?: string }>();
  const { data, isLoading: statusLoading, isError } = useQuery<{ enabled: boolean }>({
    queryKey: ["/api/brands", brandId, "creative-performance/status"],
    queryFn: async () => {
      const res = await fetch(`/api/brands/${brandId}/creative-performance/status`);
      if (!res.ok) throw new Error("Not allowed");
      return res.json();
    },
    enabled: !!brandId && !!user && !authLoading,
    retry: false,
    staleTime: 30_000,
  });
  // Render nothing while auth or the status check is in flight.
  if (authLoading || (!!brandId && !!user && statusLoading)) return null;
  if (isError || !data?.enabled) {
    return <Redirect to={brandId ? `/brands/${brandId}` : "/"} />;
  }
  return <>{children}</>;
}

// Ask the gated endpoint rather than trusting a cached brand record. A denied
// brand (including a master viewing a disabled brand) never mounts the brief.
export function PerformanceBriefGuard({ children }: { children: React.ReactNode }) {
  const { user, isLoading: authLoading } = useAuth();
  const { brandId } = useParams<{ brandId?: string }>();
  const { data, isPending, isError, isFetchedAfterMount } = useQuery<{ metrics: unknown }>({
    queryKey: ["/api/brands", brandId, "ads-performance", "capabilities", user?.id],
    queryFn: async () => {
      const response = await fetch(`/api/brands/${brandId}/ads-performance/capabilities`, { credentials: "include" });
      if (!response.ok) throw new Error("Performance Brief is unavailable");
      return response.json();
    },
    enabled: !!brandId && !!user && !authLoading,
    retry: false,
    staleTime: 0,
    refetchOnMount: "always",
  });
  if (authLoading || (user && brandId && (isPending || !isFetchedAfterMount))) return null;
  if (!user || !brandId || isError || !data) return <Redirect to={user && brandId ? `/brands/${brandId}` : "/"} />;
  return <>{children}</>;
}

export function VideoReferenceAdsGuard({ children }: { children: React.ReactNode }) {
  const { user, isLoading: authLoading } = useAuth();
  const { brandId } = useParams<{ brandId?: string }>();
  const {
    data,
    error,
    isLoading,
    isFetching,
    isFetchedAfterMount,
    isError,
    refetch,
  } = useQuery<{ enabled: boolean }>({
    queryKey: ["/api/brands", brandId, "video-reference-ads", "availability", user?.id],
    queryFn: async () => {
      const response = await fetch(`/api/brands/${brandId}/video-reference-ads/availability`);
      if (!response.ok) {
        throw new Error(`The availability check failed (${response.status}).`);
      }
      let payload: unknown;
      try {
        payload = await response.json();
      } catch {
        throw new Error("The availability service returned an invalid response.");
      }
      return parseVideoReferenceAvailability(payload);
    },
    enabled: !!brandId && !!user?.isMaster && !authLoading,
    retry: false,
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: "always",
  });
  // Never mount from a previously cached `enabled: true`: every mount/focus
  // revalidates, and the guarded page remains unmounted while that request is
  // in flight. Every non-allow state is visible and recoverable.
  const guardState = videoReferenceAvailabilityGuardState({
    brandId,
    authLoading,
    isMaster: user?.isMaster === true,
    statusLoading: isLoading,
    statusFetching: isFetching,
    statusFetchedAfterMount: isFetchedAfterMount,
    statusError: isError,
    enabled: data?.enabled,
  });
  if (guardState === "pending") {
    return (
      <div
        className="min-h-screen flex items-center justify-center p-6"
        data-testid="video-reference-availability-loading"
      >
        <LoaderCircle
          className="h-5 w-5 animate-spin text-muted-foreground"
          role="status"
          aria-label="Loading Video Reference Ads"
        />
      </div>
    );
  }
  if (guardState === "unavailable") {
    return <Redirect to={brandId ? `/brands/${brandId}` : "/"} />;
  }
  if (guardState === "error") {
    const diagnostic = error instanceof Error
      ? error.message
      : "The availability check could not be completed.";
    return (
      <div
        className="min-h-screen flex items-center justify-center p-6"
        data-testid="video-reference-availability-error"
      >
        <div className="w-full max-w-md rounded-lg border bg-card p-6 text-center space-y-4">
          <AlertCircle className="mx-auto h-8 w-8 text-destructive" />
          <div className="space-y-2">
            <h1 className="text-lg font-semibold">Couldn’t check access</h1>
            <p className="text-sm text-muted-foreground">
              Video Reference Ads may still be available. Retry the check or return to the brand.
            </p>
            <p className="text-xs text-muted-foreground" data-testid="video-reference-availability-diagnostic">
              {diagnostic}
            </p>
          </div>
          <div className="flex justify-center gap-2">
            <Button
              type="button"
              onClick={() => void refetch()}
              data-testid="button-retry-video-reference-availability"
            >
              Retry
            </Button>
            <Button asChild variant="outline">
              <Link href={brandId ? `/brands/${brandId}` : "/"}>Back to brand</Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }
  return <>{children}</>;
}
// Shown to a signed-in user who lacks master/superuser access. Anonymous
// visitors to these deep-links are already bounced to /login upstream (see the
// top-level `!user` handling), so this guard only ever renders for a logged-in
// user — meaning a redirect fire here is always an authorization gap, which
// should surface as an explicit "no permission" screen rather than a silent
// bounce home.
function NoPermissionScreen() {
  return (
    <div
      className="min-h-screen flex items-center justify-center p-6"
      data-testid="screen-no-permission"
    >
      <div className="max-w-md text-center space-y-3">
        <Lock className="h-8 w-8 mx-auto text-muted-foreground" />
        <h1 className="text-xl font-semibold">You don't have permission to view this page</h1>
        <p className="text-sm text-muted-foreground">
          This page is restricted to administrators. If you think you should have access, contact a
          workspace admin.
        </p>
        <Button asChild variant="outline" data-testid="button-back-home">
          <Link href="/">Back to home</Link>
        </Button>
      </div>
    </div>
  );
}

export function MasterOrSuperuserGuard({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();
  if (isLoading) return null;
  if (!user?.isMaster && !user?.isSuperuser) {
    return <NoPermissionScreen />;
  }
  return <>{children}</>;
}

export function MasterOnlyGuard({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();
  if (isLoading) return null;
  if (user?.isMaster !== true) return <NoPermissionScreen />;
  return <>{children}</>;
}
export function LegacyTemplateStudioRedirect() {
  const [path] = useLocation();
  return <Redirect replace to={globalTemplateStudioPath(path) || LP_TEMPLATE_STUDIO_PATH} />;
}
const CreativeTinderGuard = AdminOnlyGuard;

// Master-OR-brand-flag guard for tools rolled out per brand via the admin
// Brand Feature Flags tab (UGC Express, Video Express Max). Masters always
// pass; non-masters pass only when the brand's settings flag is enabled.
// Server-side middleware enforces the same rule, so this is UX-only gating.
// Pass masterBypass={false} to make the flag authoritative for masters too
// (Ad Launcher — the admin setting gates everyone).
export function BrandFlagGuard({ flag, children, masterBypass = true }: { flag: string; children: React.ReactNode; masterBypass?: boolean }) {
  const { user, isLoading } = useAuth();
  const { brandId } = useParams<{ brandId?: string }>();
  const { data: brand, isLoading: brandLoading } = useQuery<Brand>({
    queryKey: ["/api/brands", brandId],
    queryFn: getQueryFn({ on401: "returnNull" }),
    enabled: !!brandId && !!user && !(masterBypass && user.isMaster),
  });
  if (isLoading) return null;
  if (masterBypass && user?.isMaster) return <>{children}</>;
  if (!brandId) return <Redirect to="/" />;
  if (brandLoading) return null;
  const settings = (brand?.settings || {}) as Record<string, boolean>;
  if (!settings[flag]) {
    return <Redirect to={`/brands/${brandId}`} />;
  }
  return <>{children}</>;
}

export function BetaFeatureGuard({ flag, children }: { flag: string; children: React.ReactNode }) {
  const { user, isLoading: authLoading } = useAuth();
  const { brandId } = useParams<{ brandId?: string }>();
  const { data: flags, isPending, isFetchedAfterMount, error } = useQuery<Record<string, boolean>>({
    queryKey: [`/api/brands/${brandId}/beta-features`],
    queryFn: async () => {
      const response = await fetch(`/api/brands/${brandId}/beta-features`, { credentials: "include" });
      if (!response.ok) throw new Error(`Could not verify beta access (${response.status}).`);
      return response.json();
    },
    enabled: !!brandId && !!user && !authLoading,
    retry: false,
    staleTime: 0,
    refetchOnMount: "always",
  });
  // A cold query can be pending before it starts fetching. Cached flags can
  // also predate a new grant: decide only after this mount's check completes.
  if (authLoading || (user && brandId && (isPending || !isFetchedAfterMount))) return null;
  if (!user || !brandId || error || flags?.[flag] !== true) {
    return <Redirect to={user && brandId ? `/brands/${brandId}` : "/"} />;
  }
  return <>{children}</>;
}

export function ConceptBoardsGuard({ children }: { children: React.ReactNode }) {
  const { user, isLoading: authLoading } = useAuth();
  const { brandId } = useParams<{ brandId?: string }>();
  const { data, isPending, isFetchedAfterMount, isError } = useQuery<{ enabled: boolean }>({
    queryKey: [`/api/brands/${brandId}/concept-boards/access`, user?.id],
    queryFn: async () => {
      const response = await fetch(`/api/brands/${brandId}/concept-boards/access`, { credentials: "include" });
      if (!response.ok) throw new Error("Concept Boards unavailable");
      return response.json();
    },
    enabled: !!brandId && user?.isMaster === true && !authLoading,
    retry: false,
    staleTime: 0,
    refetchOnMount: "always",
  });
  if (authLoading || (user?.isMaster && brandId && (isPending || !isFetchedAfterMount))) return null;
  if (!user?.isMaster || !brandId || isError || data?.enabled !== true)
    return <Redirect to={user && brandId ? `/brands/${brandId}` : "/"} />;
  return <>{children}</>;
}

function AppLayout({ children }: { children: React.ReactNode }) {
  return (
    <SidebarProvider style={{ "--sidebar-width": "16rem", "--sidebar-width-icon": "3rem" } as React.CSSProperties}>
      <div className="flex h-screen w-full">
        <AppSidebar />
        <div className="flex flex-col flex-1 min-w-0">
          <header className="flex items-center justify-between p-2 border-b shrink-0 h-12">
            <SidebarTrigger data-testid="button-sidebar-toggle" />
            <NotificationBell />
          </header>
          <main className="flex-1 min-h-0 overflow-y-auto">
            {children}
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}

function AuthenticatedRouter() {
  return (
    <Switch>
      <Route path="/">{() => <AppLayout><DashboardPage /></AppLayout>}</Route>
      <Route path="/admin">{() => <AppLayout><AdminPage /></AppLayout>}</Route>
      <Route path="/admin/text-agent">{() => <AdminOnlyGuard><AppLayout><AdminTextAgentPage /></AppLayout></AdminOnlyGuard>}</Route>
      <Route path="/messaging/results/:id">{() => <AppLayout><MessagingResultPage /></AppLayout>}</Route>
      <Route path="/admin/smb">{() => <AdminOnlyGuard><SmbAdminGuard><AppLayout><AdminSmbPage /></AppLayout></SmbAdminGuard></AdminOnlyGuard>}</Route>
      <Route path="/admin/smb/orders/:orderId/:step">{() => <AdminOnlyGuard><SmbAdminGuard><AppLayout><AdminSmbPage /></AppLayout></SmbAdminGuard></AdminOnlyGuard>}</Route>
      <Route path="/admin/smb/orders/:orderId">{() => <AdminOnlyGuard><SmbAdminGuard><AppLayout><AdminSmbPage /></AppLayout></SmbAdminGuard></AdminOnlyGuard>}</Route>
      <Route path="/admin/smb/packages/:packageId">{() => <AdminOnlyGuard><SmbAdminGuard><AppLayout><AdminSmbPage /></AppLayout></SmbAdminGuard></AdminOnlyGuard>}</Route>
      <Route path="/admin/smb/packages">{() => <AdminOnlyGuard><SmbAdminGuard><AppLayout><AdminSmbPage /></AppLayout></SmbAdminGuard></AdminOnlyGuard>}</Route>
      <Route path="/admin/campaign-alerts">{() => <AdminOnlyGuard><AppLayout><AdminCampaignAlertsPage /></AppLayout></AdminOnlyGuard>}</Route>
      <Route path="/brands-dashboard">{() => <AppLayout><MasterOnlyGuard><BrandsDashboardPage /></MasterOnlyGuard></AppLayout>}</Route>
      <Route path="/admin/leads">{() => <AdminOnlyGuard><AppLayout><AdminLeadsPage /></AppLayout></AdminOnlyGuard>}</Route>
      <Route path="/admin/beta-features">{() => <AdminOnlyGuard><AppLayout><AdminBetaFeaturesPage /></AppLayout></AdminOnlyGuard>}</Route>
      <Route path="/admin/shopify-image-repair">{() => <AdminOnlyGuard><AppLayout><AdminShopifyImageRepairPage /></AppLayout></AdminOnlyGuard>}</Route>
      <Route path="/brands/:brandId/template-studio/types">{() => <AdminOnlyGuard><ConceptBoardsGuard><BrandPageWrapper><AdminTemplateStudioTypesPage /></BrandPageWrapper></ConceptBoardsGuard></AdminOnlyGuard>}</Route>
      <Route path="/brands/:brandId/template-studio/library">{() => <AdminOnlyGuard><ConceptBoardsGuard><BrandPageWrapper><BoardReferenceLibraryPage /></BrandPageWrapper></ConceptBoardsGuard></AdminOnlyGuard>}</Route>
      <Route path="/brands/:brandId/template-studio/boards">{() => <AdminOnlyGuard><ConceptBoardsGuard><BrandPageWrapper><ConceptBoardsPage /></BrandPageWrapper></ConceptBoardsGuard></AdminOnlyGuard>}</Route>
      <Route path="/internal/onboarding">{() => <AdminOnlyGuard><AppLayout><InternalOnboardingPage /></AppLayout></AdminOnlyGuard>}</Route>
      <Route path="/internal/mcp-console">{() => <AdminOnlyGuard><AppLayout><InternalMcpConsolePage /></AppLayout></AdminOnlyGuard>}</Route>
      {/* Global master authoring is independent of consumer LP2 beta grants. */}
      <Route path="/lp-express-template-studio/new">{() => <MasterOnlyGuard><AppLayout><TemplateStudioPage /></AppLayout></MasterOnlyGuard>}</Route>
      <Route path="/lp-express-template-studio/sessions/:sessionId">{(params) => <MasterOnlyGuard><AppLayout><TemplateStudioPage routeSessionId={params.sessionId} /></AppLayout></MasterOnlyGuard>}</Route>
      <Route path="/lp-express-template-studio">{() => <MasterOnlyGuard><AppLayout><TemplateStudioLibraryPage /></AppLayout></MasterOnlyGuard>}</Route>
      <Route path="/brands/:brandId/lp-express-template-studio/new">{() => <MasterOnlyGuard><LegacyTemplateStudioRedirect /></MasterOnlyGuard>}</Route>
      <Route path="/brands/:brandId/lp-express-template-studio/sessions/:sessionId">{() => <MasterOnlyGuard><LegacyTemplateStudioRedirect /></MasterOnlyGuard>}</Route>
      <Route path="/brands/:brandId/lp-express-template-studio">{() => <MasterOnlyGuard><LegacyTemplateStudioRedirect /></MasterOnlyGuard>}</Route>
      <Route path="/profile">{() => <AppLayout><ProfilePage /></AppLayout>}</Route>
      <Route path="/security">{() => <AppLayout><SecurityPage /></AppLayout>}</Route>
      <Route path="/usage">{() => <AppLayout><UsageLogsPage /></AppLayout>}</Route>
      <Route path="/brands">{() => <AppLayout><BrandsPage /></AppLayout>}</Route>
      <Route path="/brands/new">{() => <AppLayout><BrandWizardPage /></AppLayout>}</Route>
      <Route path="/brands/:brandId/settings">{() => <BrandPageWrapper><BrandWizardPage /></BrandPageWrapper>}</Route>
      <Route path="/brands/:brandId/growth">{() => <BrandPageWrapper><GrowthOperationsPage /></BrandPageWrapper>}</Route>
      <Route path="/brands/:brandId">{() => <BrandPageWrapper><BrandDetailPage /></BrandPageWrapper>}</Route>
      <Route path="/brands/:brandId/customer-evidence">{() => <BrandPageWrapper><CustomerEvidencePage /></BrandPageWrapper>}</Route>
      <Route path="/brands/:brandId/personas">{() => <BrandPageWrapper><PersonasPage /></BrandPageWrapper>}</Route>
      <Route path="/brands/:brandId/pain-pillars">{() => <BrandPageWrapper><PainPillarsPage /></BrandPageWrapper>}</Route>
      <Route path="/brands/:brandId/personas/new">{() => <BrandPageWrapper><PersonaCreatePage /></BrandPageWrapper>}</Route>
      <Route path="/brands/:brandId/personas/:personaId">{() => <BrandPageWrapper><PersonaDetailPage /></BrandPageWrapper>}</Route>
      <Route path="/brands/:brandId/products">{() => <BrandPageWrapper><ProductsPage /></BrandPageWrapper>}</Route>
      <Route path="/brands/:brandId/products/new">{() => <BrandPageWrapper><ProductCreatePage key="new" mode="create" /></BrandPageWrapper>}</Route>
      <Route path="/brands/:brandId/products/:productId/edit">{(params) => <BrandPageWrapper><ProductCreatePage key={params.productId} mode="edit" productIdProp={params.productId} /></BrandPageWrapper>}</Route>
      <Route path="/brands/:brandId/products/:productId">{() => <BrandPageWrapper><ProductDetailPage /></BrandPageWrapper>}</Route>
      <Route path="/brands/:brandId/narratives">{() => <BrandPageWrapper><NarrativesPage /></BrandPageWrapper>}</Route>
      <Route path="/brands/:brandId/narrative-generations/:generationId">{() => <BrandPageWrapper><NarrativeGenerationDetailPage /></BrandPageWrapper>}</Route>
      <Route path="/brands/:brandId/narrative-angles/:angleId">{() => <BrandPageWrapper><NarrativeAngleDetailPage /></BrandPageWrapper>}</Route>
      <Route path="/brands/:brandId/campaigns">{() => <BrandPageWrapper><CampaignsPage /></BrandPageWrapper>}</Route>
      <Route path="/brands/:brandId/ugc-campaigns">{() => <AdminOnlyGuard><BrandPageWrapper><UgcCampaignsPage /></BrandPageWrapper></AdminOnlyGuard>}</Route>
      <Route path="/brands/:brandId/ugc-campaigns/new">{() => <AdminOnlyGuard><BrandPageWrapper><UgcCampaignCreatePage /></BrandPageWrapper></AdminOnlyGuard>}</Route>
      <Route path="/brands/:brandId/ugc-campaigns/:ugcCampaignId">{() => <AdminOnlyGuard><BrandPageWrapper><UgcCampaignDetailPage /></BrandPageWrapper></AdminOnlyGuard>}</Route>
      <Route path="/brands/:brandId/claymation-campaigns">{() => <AdminOnlyGuard><BrandPageWrapper><ClaymationCampaignsPage /></BrandPageWrapper></AdminOnlyGuard>}</Route>
      <Route path="/brands/:brandId/claymation-campaigns/new">{() => <AdminOnlyGuard><BrandPageWrapper><ClaymationCampaignCreatePage /></BrandPageWrapper></AdminOnlyGuard>}</Route>
      <Route path="/brands/:brandId/claymation-campaigns/:claymationCampaignId">{() => <AdminOnlyGuard><BrandPageWrapper><ClaymationCampaignDetailPage /></BrandPageWrapper></AdminOnlyGuard>}</Route>
      <Route path="/brands/:brandId/video-express-campaigns">{() => <BrandFlagGuard flag="enableVideoExpressMax"><BrandPageWrapper><VideoExpressCampaignsPage /></BrandPageWrapper></BrandFlagGuard>}</Route>
      <Route path="/brands/:brandId/video-express-campaigns/:videoExpressCampaignId">{() => <BrandFlagGuard flag="enableVideoExpressMax"><BrandPageWrapper><VideoExpressCampaignEditPage /></BrandPageWrapper></BrandFlagGuard>}</Route>
      <Route path="/brands/:brandId/pixar-campaigns">{() => <AdminOnlyGuard><BrandPageWrapper><PixarCampaignsPage /></BrandPageWrapper></AdminOnlyGuard>}</Route>
      <Route path="/brands/:brandId/pixar-campaigns/new">{() => <AdminOnlyGuard><BrandPageWrapper><PixarCampaignCreatePage /></BrandPageWrapper></AdminOnlyGuard>}</Route>
      <Route path="/brands/:brandId/pixar-campaigns/:pixarCampaignId">{() => <AdminOnlyGuard><BrandPageWrapper><PixarCampaignDetailPage /></BrandPageWrapper></AdminOnlyGuard>}</Route>

      <Route path="/brands/:brandId/simpsons-campaigns">{() => <AdminOnlyGuard><BrandPageWrapper><SimpsonsCampaignsPage /></BrandPageWrapper></AdminOnlyGuard>}</Route>
      <Route path="/brands/:brandId/simpsons-campaigns/new">{() => <AdminOnlyGuard><BrandPageWrapper><SimpsonsCampaignCreatePage /></BrandPageWrapper></AdminOnlyGuard>}</Route>
      <Route path="/brands/:brandId/simpsons-campaigns/:simpsonsCampaignId">{() => <AdminOnlyGuard><BrandPageWrapper><SimpsonsCampaignDetailPage /></BrandPageWrapper></AdminOnlyGuard>}</Route>
      <Route path="/brands/:brandId/dayinlife-campaigns">{() => <AdminOnlyGuard><BrandPageWrapper><DayinlifeCampaignsPage /></BrandPageWrapper></AdminOnlyGuard>}</Route>
      <Route path="/brands/:brandId/dayinlife-campaigns/new">{() => <AdminOnlyGuard><BrandPageWrapper><DayinlifeCampaignCreatePage /></BrandPageWrapper></AdminOnlyGuard>}</Route>
      <Route path="/brands/:brandId/dayinlife-campaigns/:dayinlifeCampaignId">{() => <AdminOnlyGuard><BrandPageWrapper><DayinlifeCampaignDetailPage /></BrandPageWrapper></AdminOnlyGuard>}</Route>
      <Route path="/brands/:brandId/persona-intelligence">{() => <BrandPageWrapper><PersonaLabDashboardPage /></BrandPageWrapper>}</Route>
      <Route path="/brands/:brandId/persona-intelligence/flow">{() => <BrandPageWrapper><PersonaIntelligencePage /></BrandPageWrapper>}</Route>
      <Route path="/brands/:brandId/persona-intelligence/flow/:flowId">{() => <BrandPageWrapper><PersonaIntelligencePage /></BrandPageWrapper>}</Route>
      <Route path="/landing-page-templates">{() => <AppLayout><LandingPageTemplatesPage /></AppLayout>}</Route>
      <Route path="/landing-page-templates/:id">{() => <AppLayout><LandingPageTemplateDetailPage /></AppLayout>}</Route>
      {/* Story Categories & Video Styles now live inside Admin → Meme Settings.
          Old bookmarks redirect there (guard preserved so non-admins see the
          no-permission screen instead of bouncing through /admin). */}
      <Route path="/story-categories">{() => <MasterOrSuperuserGuard><Redirect to="/admin?tab=story-video-styles&sub=story-categories" /></MasterOrSuperuserGuard>}</Route>
      {/* Deep-link-only internal changelog — intentionally NO sidebar/nav entry. */}
      <Route path="/internal/updates">{() => <MasterOrSuperuserGuard><AppLayout><InternalUpdatesPage /></AppLayout></MasterOrSuperuserGuard>}</Route>
      <Route path="/video-express-styles">{() => <MasterOrSuperuserGuard><Redirect to="/admin?tab=story-video-styles&sub=video-styles" /></MasterOrSuperuserGuard>}</Route>
      <Route path="/brands/:brandId/landing-pages">{() => <BrandPageWrapper><LandingPagesPage /></BrandPageWrapper>}</Route>
      <Route path="/brands/:brandId/landing-pages/:id">{() => <BrandPageWrapper><LandingPageDetailPage /></BrandPageWrapper>}</Route>
      <Route path="/brands/:brandId/campaigns/:campaignId">{() => <BrandPageWrapper><CampaignDetailPage /></BrandPageWrapper>}</Route>
      <Route path="/ads-performance">{() => <AppLayout><AdsPerformanceOverviewPage /></AppLayout>}</Route>
      <Route path="/brands/:brandId/ads-performance/settings">{() => <PerformanceBriefGuard><BrandPageWrapper><AdsPerformanceBriefPage /></BrandPageWrapper></PerformanceBriefGuard>}</Route>
      <Route path="/brands/:brandId/ads-performance">{() => <PerformanceBriefGuard><BrandPageWrapper><AdsPerformanceBriefPage /></BrandPageWrapper></PerformanceBriefGuard>}</Route>
      <Route path="/brands/:brandId/meta-ads">{() => <BrandPageWrapper><MetaAdsIntegrationPage /></BrandPageWrapper>}</Route>
      <Route path="/brands/:brandId/ad-launcher">{() => (
        <BrandFlagGuard flag="enableAdLauncher" masterBypass={false}>
          <BrandPageWrapper>
            <Suspense fallback={<div className="min-h-[50vh] bg-background" />}>
              <AdLauncherPage />
            </Suspense>
          </BrandPageWrapper>
        </BrandFlagGuard>
      )}</Route>
      <Route path="/brands/:brandId/campaign-wizard/:campaignId">{() => <BrandPageWrapper><CampaignWizardPage /></BrandPageWrapper>}</Route>
      <Route path="/brands/:brandId/campaign-wizard">{() => <BrandPageWrapper><CampaignWizardPage /></BrandPageWrapper>}</Route>
      <Route path="/brands/:brandId/ideator">{() => <BrandPageWrapper><IdeatorListPage /></BrandPageWrapper>}</Route>
      <Route path="/brands/:brandId/ideator/new">{() => <BrandPageWrapper><IdeatorCreatePage /></BrandPageWrapper>}</Route>
      <Route path="/brands/:brandId/ideator/:runId">{() => <BrandPageWrapper><IdeatorDetailPage /></BrandPageWrapper>}</Route>
      <Route path="/brands/:brandId/ad-units">{() => <BrandPageWrapper><AdUnitsPage /></BrandPageWrapper>}</Route>
      <Route path="/brands/:brandId/pixar-ad-units">{() => <BrandPageWrapper><AdUnitsPage mode="pixar" /></BrandPageWrapper>}</Route>
      <Route path="/brands/:brandId/ads-express">{() => <BrandPageWrapper><AdsExpressPage /></BrandPageWrapper>}</Route>
      <Route path="/brands/:brandId/ads-express-kc">{() => <BrandPageWrapper><AdsExpressKcPage /></BrandPageWrapper>}</Route>
      <Route path="/brands/:brandId/landing-pages-express">{() => <BrandFlagGuard flag="enableLandingPagesExpress"><BrandPageWrapper><LandingPagesExpressPage surface="lp-express-1" /></BrandPageWrapper></BrandFlagGuard>}</Route>
      <Route path={LP_EXPRESS_2_ROUTE.path}>{() => <BetaFeatureGuard {...LP_EXPRESS_2_ROUTE.gate}><BrandPageWrapper><LandingPagesExpressPage surface={LP_EXPRESS_2_ROUTE.surface} /></BrandPageWrapper></BetaFeatureGuard>}</Route>
      <Route path="/brands/:brandId/landing-pages-express/leads">{() => <AdminOnlyGuard><BrandPageWrapper><AdminLeadsPage /></BrandPageWrapper></AdminOnlyGuard>}</Route>
      <Route path="/brands/:brandId/email-express">{() => <AdminOnlyGuard><BrandPageWrapper><EmailExpressPage /></BrandPageWrapper></AdminOnlyGuard>}</Route>
      <Route path="/brands/:brandId/email-express/flows/:flowId">{() => <AdminOnlyGuard><BrandPageWrapper><EmailFlowPage /></BrandPageWrapper></AdminOnlyGuard>}</Route>
      <Route path="/brands/:brandId/email-express/:emailId">{() => <AdminOnlyGuard><BrandPageWrapper><EmailEditorPage /></BrandPageWrapper></AdminOnlyGuard>}</Route>
      {/* Email Express 2.0 — master-only, isolated from V1 above. */}
      <Route path="/brands/:brandId/email-express-2">{() => <EmailExpress2Guard><BrandPageWrapper><EmailExpress2CreatePage /></BrandPageWrapper></EmailExpress2Guard>}</Route>
      <Route path="/brands/:brandId/email-express-2/:id">{() => <EmailExpress2Guard><BrandPageWrapper><EmailExpress2EditorPage /></BrandPageWrapper></EmailExpress2Guard>}</Route>
      <Route path="/brands/:brandId/lp-express/preview">{() => <AdminOnlyGuard><BrandPageWrapper><LpExpressPreviewPage /></BrandPageWrapper></AdminOnlyGuard>}</Route>
      <Route path="/brands/:brandId/library">{() => <BrandPageWrapper><AdLibraryPage /></BrandPageWrapper>}</Route>
      {/* Weekly Deliverables — brand-flag gated via effective-flags API (fail-closed). */}
      <Route path="/brands/:brandId/deliverables/:deliveryId">{() => <WeeklyDeliverablesGuard><BrandPageWrapper><DeliverableReviewPage /></BrandPageWrapper></WeeklyDeliverablesGuard>}</Route>
      <Route path="/brands/:brandId/deliverables">{() => <WeeklyDeliverablesGuard><BrandPageWrapper><DeliverablesPage /></BrandPageWrapper></WeeklyDeliverablesGuard>}</Route>
      <Route path="/brands/:brandId/ad-animator">{() => <BrandPageWrapper><AdAnimatorPage /></BrandPageWrapper>}</Route>
      <Route path="/brands/:brandId/ugc-templates">{() => <BrandPageWrapper><UgcTemplatesPage /></BrandPageWrapper>}</Route>
      <Route path="/brands/:brandId/ad-units/:adUnitId">{() => <BrandPageWrapper><AdUnitDetailPage /></BrandPageWrapper>}</Route>
      <Route path="/brands/:brandId/offers">{() => <BrandPageWrapper><OffersPage /></BrandPageWrapper>}</Route>
      <Route path="/brands/:brandId/offers/new">{() => <BrandPageWrapper><OfferCreatePage /></BrandPageWrapper>}</Route>
      <Route path="/brands/:brandId/offers/:offerId">{() => <BrandPageWrapper><OfferDetailPage /></BrandPageWrapper>}</Route>
      <Route path="/brands/:brandId/sales-discounts">{() => <BrandPageWrapper><SalesDiscountsPage /></BrandPageWrapper>}</Route>
      <Route path="/brands/:brandId/sales-discounts/new">{() => <BrandPageWrapper><SalesDiscountCreatePage /></BrandPageWrapper>}</Route>
      <Route path="/brands/:brandId/sales-discounts/:saleId">{() => <BrandPageWrapper><SalesDiscountDetailPage /></BrandPageWrapper>}</Route>
      <Route path="/brands/:brandId/creative-tinder">{() => <CreativeTinderGuard><BrandPageWrapper><CreativeTinderPage /></BrandPageWrapper></CreativeTinderGuard>}</Route>
      <Route path="/brands/:brandId/creative-iterations">{() => <BrandAccessGuard><BrandPageWrapper><CreativeIterationsPage /></BrandPageWrapper></BrandAccessGuard>}</Route>
      {/* Creative Performance Dashboard — brand-flag + brand-access gated (server verifies via status route). */}
      <Route path="/brands/:brandId/creative-performance">{() => <CreativePerformanceGuard><BrandPageWrapper><CreativePerformancePage /></BrandPageWrapper></CreativePerformanceGuard>}</Route>
      <Route path="/brands/:brandId/ugc-express">{() => <BrandFlagGuard flag="enableUgcExpress"><BrandPageWrapper><UgcExpressPage /></BrandPageWrapper></BrandFlagGuard>}</Route>
      <Route path="/brands/:brandId/ugc-express-2">{(params) => <Redirect to={`/brands/${params.brandId}/ugc-express`} />}</Route>
      <Route path="/brands/:brandId/broll-express">{() => <BrandFlagGuard flag="enableBrollExpress"><BrandPageWrapper><BrollExpressPage /></BrandPageWrapper></BrandFlagGuard>}</Route>
      <Route path="/brands/:brandId/grwmugc-pb">{() => <BrandPageWrapper><GrwmugcPbPage /></BrandPageWrapper>}</Route>
      <Route path="/brands/:brandId/video-reference-ads">{() => <VideoReferenceAdsGuard><BrandPageWrapper><VideoReferenceAdsPage /></BrandPageWrapper></VideoReferenceAdsGuard>}</Route>
      <Route path="/brands/:brandId/video-reference-templates">{() => <VideoReferenceAdsGuard><BrandPageWrapper><VideoReferenceTemplateLibraryPage /></BrandPageWrapper></VideoReferenceAdsGuard>}</Route>
      <Route path="/brands/:brandId/slideshow-express">{() => <AdminOnlyGuard><BrandPageWrapper><SlideshowExpressPage /></BrandPageWrapper></AdminOnlyGuard>}</Route>
      <Route path="/brands/:brandId/meme-generator">{() => <BrandPageWrapper><MemeGeneratorPage /></BrandPageWrapper>}</Route>
      <Route path="/creative-tinder/settings">{() => <CreativeTinderGuard><AppLayout><CreativeTinderSettingsPage /></AppLayout></CreativeTinderGuard>}</Route>
      <Route path="/brands/:brandId/matrix">{() => <BrandPageWrapper><StrategyMatrixPage /></BrandPageWrapper>}</Route>
      <Route path="/brands/:brandId/creative-lab">{() => <BrandPageWrapper><CreativeLabPage /></BrandPageWrapper>}</Route>
      <Route path="/brands/:brandId/assets">{() => <BrandPageWrapper><AssetsPage /></BrandPageWrapper>}</Route>
      <Route path="/brands/:brandId/performance">{() => <BrandPageWrapper><PerformancePage /></BrandPageWrapper>}</Route>
      <Route path="/brands/:brandId/brand-spy">{() => <BrandPageWrapper><BrandSpyPage /></BrandPageWrapper>}</Route>
      <Route path="/brands/:brandId/learnings">{() => <BrandPageWrapper><LearningsPage /></BrandPageWrapper>}</Route>

      {/* Creative Strategist (Task #2375). masterBypass={false}: the brand flag
          is authoritative for everyone, so a brand without it redirects to the
          brand overview — matching the server, which 403s the whole API. */}
      <Route path="/brands/:brandId/creative-strategist">{() => <CreativeStrategistGuard><BrandPageWrapper><CreativeStrategistHomePage /></BrandPageWrapper></CreativeStrategistGuard>}</Route>
      <Route path="/brands/:brandId/creative-strategist/templates">{() => <CreativeStrategistGuard><BrandPageWrapper><CreativeStrategistTemplatesPage /></BrandPageWrapper></CreativeStrategistGuard>}</Route>
      <Route path="/brands/:brandId/creative-strategist/projects">{() => <CreativeStrategistGuard><BrandPageWrapper><CreativeStrategistProjectsPage /></BrandPageWrapper></CreativeStrategistGuard>}</Route>
      {/* Spec path-style tab links (…/projects/:id/tasks) fold onto the
          repo's ?tab= deep-link convention. */}
      <Route path="/brands/:brandId/creative-strategist/projects/:projectId/:tab">{() => <CreativeStrategistGuard><BrandPageWrapper><CreativeStrategistProjectPage /></BrandPageWrapper></CreativeStrategistGuard>}</Route>
      <Route path="/brands/:brandId/creative-strategist/projects/:projectId">{() => <CreativeStrategistGuard><BrandPageWrapper><CreativeStrategistProjectPage /></BrandPageWrapper></CreativeStrategistGuard>}</Route>

      {/* Standalone Creative Editor (Task #2388) — full-screen workspace
          reached from My Ads ("Edit Creative"), deliberately WITHOUT
          BrandPageWrapper/sidebar. Access is enforced server-side (brand
          resource access + creative_editor beta grant, fail-closed). */}
      <Route path="/creative-editor/:projectId">{() => (
        <Suspense fallback={<div className="min-h-screen bg-background" />}>
          <CreativeEditorPage />
        </Suspense>
      )}</Route>

      <Route component={NotFound} />
    </Switch>
  );
}

const AUTH_REQUIRED_EXACT_PATHS = new Set([
  "/admin",
  "/brands",
  "/creative-tinder/settings",
  "/landing-page-templates",
  "/lp-express-template-studio",
  "/profile",
  "/security",
  "/story-categories",
  "/usage",
  "/video-express-styles",
]);

const AUTH_REQUIRED_PATH_PREFIXES = [
  "/admin/",
  "/brands/",
  "/creative-editor/",
  "/internal/",
  "/landing-page-templates/",
  "/lp-express-template-studio/",
  "/messaging/results/",
];

export function isAuthRequiredAppLocation(location: string): boolean {
  return (
    AUTH_REQUIRED_EXACT_PATHS.has(location) ||
    AUTH_REQUIRED_PATH_PREFIXES.some((prefix) => location.startsWith(prefix))
  );
}

export function AppRouter() {
  const [location] = useLocation();
  const { user, isLoading, isReconnecting, retryAuth } = useAuth();

  if (location === "/deck") {
    return (
      <Suspense fallback={<div className="min-h-screen bg-[#0a0a0f]" />}>
        <VentureDeckPage />
      </Suspense>
    );
  }

  // Public marketing Contact page. Short-circuits BEFORE the auth check so it
  // renders for both anonymous and signed-in visitors (like /deck).
  if (location === "/contact") {
    return (
      <Suspense fallback={<div className="min-h-screen bg-white" />}>
        <ContactPage />
      </Suspense>
    );
  }

  // Public client intake lives outside auth and app chrome.
  if (location === "/intake") {
    return (
      <Suspense fallback={<div className="min-h-screen bg-background" />}>
        <ClientIntakePage />
      </Suspense>
    );
  }

  // Review-only marketing page. It intentionally short-circuits authentication
  // and is not linked from the application navigation.
  if (location === "/new-home") {
    return <HomeV2Page />;
  }

  if (location === "/sales-deck") {
    return (
      <Suspense fallback={<div className="min-h-screen bg-[#04060B]" />}>
        <SalesDeckPage />
      </Suspense>
    );
  }

  // Custom domains: the server resolves a verified user-owned hostname to the
  // brand it serves, then maps the request path (/<slug>) to one of that brand's
  // published, custom-domain-enabled pages and injects window.__LP_CUSTOM_DOMAIN__
  // into the HTML shell. When the path maps to a page, render it at that path
  // (BEFORE the auth check, like /p/ below). When the marker carries notFound
  // (bad slug, unpublished, or not opted in), render a branded not-found.
  const lpCustomDomain = (window as any).__LP_CUSTOM_DOMAIN__ as
    | { brandSlug?: string; slug?: string; notFound?: boolean }
    | undefined;
  if (lpCustomDomain?.brandSlug && lpCustomDomain?.slug) {
    return (
      <Suspense fallback={<div className="min-h-screen bg-white" />}>
        <PublicLpExpressPage
          brandSlug={lpCustomDomain.brandSlug}
          slug={lpCustomDomain.slug}
        />
      </Suspense>
    );
  }
  if (lpCustomDomain?.notFound) {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center bg-white text-center px-6"
        data-testid="status-custom-domain-notfound"
      >
        <h1 className="text-2xl font-bold text-gray-900">Page not found</h1>
        <p className="mt-2 text-gray-500">
          This page is no longer available or has not been published.
        </p>
      </div>
    );
  }

  // Public, logged-out published LP Express pages. Short-circuits BEFORE the
  // auth check so /p/:brandSlug/:slug renders without a session (like /deck).
  if (location.startsWith("/p/")) {
    return (
      <Suspense fallback={<div className="min-h-screen bg-white" />}>
        <PublicLpExpressPage />
      </Suspense>
    );
  }

  if (isLoading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-background p-6"
        data-testid="auth-loading"
        role="status"
        aria-live="polite"
      >
        <div className="flex flex-col items-center gap-3 text-center">
          <div className="h-9 w-9 animate-spin rounded-full border-4 border-primary/20 border-t-primary" aria-hidden="true" />
          <p className="text-sm font-medium text-foreground">Connecting to your workspace…</p>
          <p className="text-xs text-muted-foreground">This should only take a moment.</p>
        </div>
      </div>
    );
  }

  if (isReconnecting && !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6" data-testid="auth-reconnecting">
        <div className="w-full max-w-md rounded-lg border bg-card p-6 text-center space-y-4">
          <AlertCircle className="mx-auto h-8 w-8 text-amber-500" />
          <div className="space-y-2">
            <h1 className="text-lg font-semibold">Reconnecting to your workspace</h1>
            <p className="text-sm text-muted-foreground">
              We couldn’t confirm your session. Your page has not been signed out.
            </p>
          </div>
          <Button type="button" onClick={() => void retryAuth?.()} data-testid="button-retry-auth">
            Try again
          </Button>
        </div>
      </div>
    );
  }

  if (!user) {
    if (location === "/login") {
      return <LandingPage />;
    }
    if (location === "/") {
      return <HomeV2Page basePath="/" />;
    }
    // Deep-linked, auth-required pages should send an anonymous visitor to
    // sign in and then bounce them back to the exact link they wanted. Keep
    // unknown paths as 404s so a typo is not mistaken for a legitimate route.
    if (isAuthRequiredAppLocation(location)) {
      try {
        // wouter's location is the path only — keep the query string too so
        // e.g. a project deep-link's ?projectId survives the login bounce.
        sessionStorage.setItem("postLoginRedirect", location + window.location.search + window.location.hash);
      } catch {
        // sessionStorage unavailable — login will just land on home.
      }
      return <Redirect to="/login" />;
    }
    return <NotFound />;
  }

  if (location === "/login") {
    return <Redirect to="/brands" />;
  }

  return <AuthenticatedRouter />;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <BrandUrlRouting><AIResearchTracker /><AppRouter /></BrandUrlRouting>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
